import {
  clone,
  each,
  extend,
  filter,
  findIndex,
  get,
  isEmpty,
  isNumber,
  isObject,
  map,
  sortBy
} from 'lodash'
import store from 'store'
import * as Promise from 'bluebird'
import * as log from 'loglevel'
import util from '../../lib/util'
import {
  USER_GROUP_CODE,
  USER_PERM_CODE,
  EVENT_LEAVE_ROOM
} from '../../lib/constants'
import { POST_EVENT } from '../types/event'
import { SET_NOTIFICATIONS } from 'stores/types/notification'

import {
  GET_GROUP_USERS,
  LOGIN,
  LOGOUT,
  SET_AUTH_PROVIDER,
  SET_GROUP_USERS,
  SET_USER_PREFERENCES,
  UPDATE_ACCESS_TOKEN,
  UPDATE_CURRENT_USER
} from 'stores/types/user'

const state = {
  authProvider: null,
  currentUser: null,
  groupUsers: null,
  rawToken: null,
  refreshToken: null,
  token: null,
  userPreferences: {}
}

const PREFS_KEY = 'USER_PREFS'

const DEFAULT_USER = {
  id: 1,
  nickname: 'witt',
  email: 'witt@w.it',
  displayName: 'Ludwig Wittgenstein',
  username: 'lw',
  lastLogin: 1487209696860,
  reputation: 'gold',
  score: '78%',
  avatar: 'https://api.adorable.io/avatars/26/author.png',
  contributions: 28,
  memberSince: '21st March 2017'
}

const SORT_TYPES = {
  SORT_SUBMISSION_DESC: 0,
  SORT_QUALITY_DESC: 1,
  SORT_CREATED_DESC: 2,
  SORT_AUTHOR_ASC: 3,
  SORT_UPDATED_DESC: 4
}

const SORT_KEYS = [
  {
    label: 'Sort by submission order',
    value: SORT_TYPES.SORT_SUBMISSION_DESC,
    field:
      'type.asc,banded_rating.desc,quality.desc,rating_count.desc,lastEdited.desc'
  },
  {
    label: 'Sort by average rating',
    value: SORT_TYPES.SORT_QUALITY_DESC,
    field: 'banded_rating.desc,quality.desc,createdAt.desc'
  },
  {
    label: 'Sort by creation date',
    value: SORT_TYPES.SORT_CREATED_DESC,
    field: 'createdAt.desc'
  },
  {
    label: 'Sort by recent change',
    value: SORT_TYPES.SORT_UPDATED_DESC,
    field: 'lastEdited.desc,createdAt.desc'
  },
  {
    label: 'Sort by author',
    value: SORT_TYPES.SORT_AUTHOR_ASC,
    field: 'displayName,createdAt.desc'
  }
]

const FILTER_TYPES = {
  FILTER_ALL_PERIODS: 0,
  FILTER_PAST_20MINS: 1,
  FILTER_PAST_1HR: 2,
  FILTER_PAST_3HRS: 3,
  FILTER_PAST_12HRS: 4,
  FILTER_PAST_24HRS: 5
}

const FILTER_KEYS = [
  {
    label: 'Show all periods',
    value: FILTER_TYPES.FILTER_ALL_PERIODS
  },
  {
    label: 'Show past 20 mins',
    value: FILTER_TYPES.FILTER_PAST_20MINS
  },
  {
    label: 'Show past 1 hour',
    value: FILTER_TYPES.FILTER_PAST_1HR
  },
  {
    label: 'Show past 3 hours',
    value: FILTER_TYPES.FILTER_PAST_3HRS
  },
  {
    label: 'Show past 12 hours',
    value: FILTER_TYPES.FILTER_PAST_12HRS
  },
  {
    label: 'Show past 24 hours',
    value: FILTER_TYPES.FILTER_PAST_24HRS
  }
]

const FILTER_RESPONSE_TYPES = {
  FILTER_BY_BOTH: 0,
  FILTER_BY_REPORTS: 1,
  FILTER_BY_RESOURCES: 2
}

const FILTER_RESPONSE_TYPES_KEYS = [
  {
    label: 'Show all types',
    value: FILTER_RESPONSE_TYPES.FILTER_BY_BOTH
  },
  {
    label: 'Show reports only',
    value: FILTER_RESPONSE_TYPES.FILTER_BY_REPORTS
  },
  {
    label: 'Show resources only',
    value: FILTER_RESPONSE_TYPES.FILTER_BY_RESOURCES
  }
]

const defaultLocalStorageKeys = [
  'authProvider',
  'currentUser',
  'jwt',
  'rawToken',
  'refreshToken',
  'vapidPublicKey',
  PREFS_KEY
]

function makeToken(token) {
  return 'Bearer ' + token
}

function getOptions(ctx) {
  return {
    headers: {
      Authorization: ctx.rootState.user.token
    }
  }
}

// getters
const getters = {
  isAuthenticated(state, getters, rootState, rootGetters) {
    if (rootGetters && rootGetters.isRemote) {
      if (state.currentUser !== null) {
        return true
      }
      return false
    }
    return true
  },
  /**
   * Check if current user is admin user
   */
  isAdmin(state, getters) {
    if (getters.isAuthenticated && state.currentUser) {
      const { userGroups } = state.currentUser
      const index = findIndex(userGroups, { code: USER_GROUP_CODE.ADMIN })
      return index !== -1
    } else {
      return false
    }
  },
  /*
   * Check if current user can create new problems
   */
  canWriteQuestion(state, getters) {
    if (getters.isAuthenticated && state.currentUser) {
      const { permissions, userGroups } = state.currentUser
      // either has ADD_PROBLEM permission or in ADMIN group
      return (
        findIndex(permissions, { code: USER_PERM_CODE.ADD_PROBLEM }) !== -1 ||
        findIndex(userGroups, { code: USER_GROUP_CODE.ADMIN }) !== -1
      )
    } else {
      return false
    }
  },
  /*
   * Check if current user can create new problems
   */
  canCreateQuestion(state, getters) {
    return getters.canWriteQuestion
  },
  canEditQuestion(state, getters) {
    return getters.canWriteQuestion
  },
  authRequired(state, getters, rootState, rootGetters) {
    return rootGetters && rootGetters.isRemote
  },
  currentUser(state, getters) {
    if (getters.isAuthenticated) {
      return state.currentUser || DEFAULT_USER
    }
    return null
  },
  currentUserGroups(state) {
    let currentUser = state.currentUser
    let userGroups = []
    if (currentUser) {
      let groups = currentUser.userGroups
      if (groups) {
        each(groups, function(group, key) {
          let code, id, title
          if (isObject(group)) {
            code = group.code
            id = group.id
            title = group.title
            if (!code) {
              code = key
            }
            if (!id) {
              id = key
            }
          } else {
            // assume group is string
            code = group
            if (isNumber(key)) {
              id = group
            } else {
              id = key
            }
          }
          userGroups.push({ id, code, title })
        })
        return sortBy(userGroups, 'title')
      }
    }
    return null
  },
  token(state) {
    return state.token
  },
  refreshToken(state) {
    return state.refreshToken
  },
  user: state => state.currentUser,
  rawToken: state => state.rawToken,
  userPreferences(state, getters, rootState) {
    if (isEmpty(state.userPreferences)) {
      let preferences = rootState.localStore.get(PREFS_KEY)
      preferences = extend(preferences, {
        sortTypes: SORT_TYPES,
        sortKeys: SORT_KEYS
      })
      return preferences || {}
    } else {
      return state.userPreferences
    }
  },
  authProvider: state => state.authProvider,
  authOptions(state, getters) {
    return {
      headers: {
        Authorization: getters.token
      }
    }
  },
  authRefreshOptions(state, getters) {
    return {
      headers: {
        Authorization: getters.refreshToken
      }
    }
  },
  responseFilter() {
    return {
      FILTER_TYPES,
      FILTER_KEYS,
      FILTER_RESPONSE_TYPES,
      FILTER_RESPONSE_TYPES_KEYS
    }
  },
  browser() {
    return util.browser
  },
  /**
   * Get current route path
   * @return {string} route path
   */
  routePath: (state, getters, rootState) => {
    return rootState.route.path
  },
  /**
   * A list of users belongs current group
   */
  groupUsers: state => state.groupUsers
}

const actions = {
  /**
   * checkAuthState
   * @param  {object} ctx
   * @return {Promise} Promise object represents if current user is authrized
   */
  async checkAuthState(ctx) {
    let state = ctx.state

    if (!ctx.getters.authRequired) {
      return true
    }

    let authProvider = state.authProvider || store.get('authProvider')
    let result = {}

    try {
      let isAuthenticated = await ctx.dispatch('checkPersistedAuthState')
      let currentUser

      if (authProvider === 'local') {
        if (isAuthenticated) {
          let token = ctx.state.token
          currentUser = await ctx.rootState.api.getCurrentUser({ token })
        } else {
          return false
        }
      } else {
        // authProvider: 'google' or 'tne'
        if (isAuthenticated) {
          currentUser = await ctx.rootState.api.getCurrentUser({
            token: ctx.state.token
          })
        } else {
          let data = await ctx.rootState.api.getOauthToken({
            provider: authProvider
          })
          let rawToken = data.token
          let token = makeToken(data.token)
          let refreshToken = makeToken(get(data, 'refreshToken', null))
          store.set('jwt', token)
          store.set('rawToken', rawToken)
          store.set('refreshToken', refreshToken)
          let vapidPublicKey = get(data, 'vapidPublicKey', null)
          await ctx.dispatch('setVapidPublicKey', vapidPublicKey)
          currentUser = await ctx.rootState.api.getCurrentUser({ token })
          result.token = token
          result.rawToken = rawToken
          result.refreshToken = refreshToken
          result.currentUser = currentUser
        }
      }

      let updates = currentUser
      await ctx.commit(UPDATE_CURRENT_USER, { updates })
      if (authProvider !== 'local') {
        await ctx.commit(LOGIN, result)
      }
      if (
        !ctx.getters.browser.versions.isMobile &&
        currentUser.notificationEnabled
      ) {
        ctx.dispatch('subscribe')
      }

      return true
    } catch (err) {
      log.error(err)
      return false
    }
  },
  checkPersistedAuthState(ctx) {
    let token = store.get('jwt')
    let rawToken = store.get('rawToken')
    let refreshToken = store.get('refreshToken')
    let currentUser = store.get('currentUser')
    if (currentUser && token && rawToken && refreshToken) {
      ctx.commit(LOGIN, {
        currentUser,
        token,
        rawToken,
        refreshToken
      })
      return true
    }
    return false
  },
  /**
   * Get all user groups information
   * @param  {Object} ctx - store object
   * @return {Promise<Array<{ code: string, created_at: number, groupSize: number, id: UUID, title: string }>>} - a list of user group object
   */
  async getAllUserGroups(ctx) {
    const options = {
      options: getOptions(ctx)
    }
    return await ctx.rootState.api.getAllUserGroups(options)
  },

  getUserStats(ctx) {
    // let token = store.get('jwt')
    let currentUser = state.currentUser
    return ctx.rootState.api
      .getUserStats({
        user: currentUser,
        options: ctx.getters.authOptions
      })
      .then(function(stats) {
        return stats
      })
  },

  /**
   * Update current user info
   * @param  {object}  ctx     - vuex store object
   * @param  {object}  updates - updates for user object
   * @return {Promise}
   */
  async updateCurrentUser(ctx, updates) {
    const object = clone(state.currentUser)
    const options = getOptions(ctx)
    const params = {
      updates,
      object,
      options
    }
    const updatedUser = await ctx.rootState.api.updateUser(params)
    if (updatedUser) {
      ctx.commit(UPDATE_CURRENT_USER, {
        updates: updatedUser
      })
    }
    store.set('currentUser', state.currentUser)
  },
  login(ctx, params) {
    let result = {}
    ctx.commit(SET_AUTH_PROVIDER, 'local')
    return ctx.rootState.api
      .authenticate(params)
      .then(function(data) {
        result.rawToken = data.token
        result.token = makeToken(data.token)
        result.refreshToken = makeToken(get(data, 'refreshToken', null))
        store.set('jwt', result.token)
        store.set('rawToken', result.rawToken)
        store.set('refreshToken', result.refreshToken)

        let vapidPublicKey = get(data, 'vapidPublicKey', null)
        ctx.dispatch('setVapidPublicKey', vapidPublicKey)

        return ctx.rootState.api.getCurrentUser(result)
      })
      .then(function(currentUser) {
        result.currentUser = currentUser
        store.set('currentUser', currentUser)
        ctx.commit(LOGIN, result)
      })
  },
  async hardLogin(ctx, params) {
    let result = {}
    let token = params.token
    let { username, password } = params

    ctx.commit(SET_AUTH_PROVIDER, 'local')

    if (!isEmpty(token)) {
      result.rawToken = token
      result.token = makeToken(token)
      store.set('jwt', result.token)
      store.set('rawToken', result.rawToken)
      store.set('refreshToken', result.rawToken)
    } else {
      const credential = {
        credential: {
          username: username,
          password: password
        }
      }
      const data = await ctx.rootState.api.authenticate(credential)
      result.rawToken = data.token
      result.token = makeToken(data.token)
      result.refreshToken = makeToken(get(data, 'refreshToken', null))
      store.set('jwt', result.token)
      store.set('rawToken', result.rawToken)
      store.set('refreshToken', result.refreshToken)
      let vapidPublicKey = get(data, 'vapidPublicKey', null)
      await ctx.dispatch('setVapidPublicKey', vapidPublicKey)
    }
    const currentUser = await ctx.rootState.api.getCurrentUser(result)
    result.currentUser = currentUser
    store.set('currentUser', currentUser)
    ctx.commit(LOGIN, result)
  },
  getNewAccessToken(ctx) {
    let refreshToken = ctx.state.refreshToken || store.get('refreshToken')
    return ctx.rootState.api
      .getNewAccessToken({
        headers: {
          Authorization: refreshToken
        }
      })
      .then(function(data) {
        let result = {}
        result.rawToken = data.token
        result.token = makeToken(data.token)
        store.set('jwt', result.token)
        store.set('rawToken', result.rawToken)
        ctx.commit(UPDATE_ACCESS_TOKEN, result)
        console.log('Access token updated: ', result.token)
        return result.token
      })
      .catch(function(err) {
        return Promise.reject(err)
      })
  },
  async logout(ctx) {
    let token = ctx.state.token || store.get('jwt')
    let refreshToken = ctx.state.refreshToken || store.get('refreshToken')
    ctx.commit('SET_PHASE', null)
    if (token) {
      try {
        // check if current route is 'problemPage' then sends 'leave_room' event
        const { route } = ctx.rootState
        const { currentUser } = ctx.rootState.user
        const { currentQuestionRoot } = ctx.rootGetters
        const {
          name,
          params: { id }
        } = route
        if (name === 'problemPage') {
          const audience = map(ctx.rootGetters.groupUsers, user => user.id)
          const payload = {
            eventType: EVENT_LEAVE_ROOM,
            chunk: {
              root: {
                id
              }
            }
          }
          await ctx.dispatch(POST_EVENT, {
            payload: extend(payload, {
              ns: {
                audience,
                message: {
                  title: '',
                  body: `${
                    currentUser.displayName
                  } is no longer working on the problem ${
                    currentQuestionRoot.title
                  }`
                }
              }
            })
          })
        }
      } catch (err) {
        log.error(err)
      }
      try {
        await ctx.dispatch('revokeSubscription')
        await ctx.dispatch('deleteVapidPublicKey')
      } catch (error) {
        log.error(error)
      }
      try {
        await ctx.rootState.api.logout(ctx.getters.authOptions)
      } catch (error) {
        log.error(error)
      }
      if (refreshToken) {
        try {
          await ctx.rootState.api.logoutRefresh(ctx.getters.authRefreshOptions)
        } catch (error) {
          log.error(error)
        }
      }
    }
    ctx.commit(SET_NOTIFICATIONS, [])
    await ctx.commit(LOGOUT)
  },
  setAuthProvider(ctx, authProvider) {
    store.set('authProvider', authProvider)
    ctx.commit(SET_AUTH_PROVIDER, authProvider)
  },
  addUser(ctx, user) {
    return ctx.rootState.api.addUser({
      user: user
    })
  },

  async saveUserPreferences(ctx, params) {
    let preferences = ctx.rootState.localStore.get(PREFS_KEY)
    preferences = extend(preferences, params, {
      sortTypes: SORT_TYPES,
      sortKeys: SORT_KEYS
    })
    await ctx.rootState.localStore.set(PREFS_KEY, preferences)
    ctx.commit(SET_USER_PREFERENCES, preferences)
  },

  async getUsers(ctx) {
    const options = {
      options: getOptions(ctx)
    }
    return await ctx.rootState.api.getUsers(options)
  },

  async resetChatLocalStorage() {
    store.each((value, key) => {
      if (!defaultLocalStorageKeys.includes(key)) {
        store.remove(key)
      }
    })
  },
  /**
   * Get users belongs to one group and update Vuex store
   * @param   {Object} ctx
   * @param   {String} id      - group id
   * @returns {Promise<Array>} - returns a list of users
   */
  async [GET_GROUP_USERS](ctx, { id }) {
    const options = {
      id,
      options: getOptions(ctx)
    }
    const currentUserId = ctx.getters.currentUser.id
    let users = await ctx.rootState.api.getGroupUsers(options)
    users = filter(users, user => user.id !== currentUserId)
    ctx.commit(SET_GROUP_USERS, users)
    return users
  }
}

const mutations = {
  UPDATE_CURRENT_USER(state, change) {
    let updates = change.updates
    state.currentUser = extend({}, state.currentUser, updates)
  },
  SET_AUTH_PROVIDER(state, authProvider) {
    store.set('authProvider', authProvider)
    state.authProvider = authProvider || 'local'
  },
  SET_USER_PREFERENCES(state, userPreferences) {
    state.userPreferences = userPreferences
  },
  SET_GROUP_USERS(state, users) {
    state.groupUsers = users
  },
  LOGIN(state, change) {
    let currentUser = change.currentUser
    let token = change.token
    let rawToken = change.rawToken
    let refreshToken = get(change, 'refreshToken', null)
    console.log('Logged in', change)
    if (currentUser && token) {
      state.currentUser = currentUser
      state.token = token
      state.rawToken = rawToken
      if (refreshToken) {
        state.refreshToken = refreshToken
      }
    }
  },
  UPDATE_ACCESS_TOKEN(state, change) {
    let token = change.token
    let rawToken = change.rawToken
    state.token = token
    state.rawToken = rawToken
  },
  /**
   * LOGOUT
   * @param {object} state
   */
  LOGOUT(state) {
    store.clearAll()
    state.authProvider = null
    state.currentUser = null
    state.token = null
    state.rawToken = null
    state.refreshToken = null
    state.userPreferences = {}
  }
}

export default {
  getters,
  state,
  mutations,
  actions
}
